// Shorthand numeric entry — the shared parser behind every money input.
//
// Dealmakers think and talk in "4M" and "100k", and until now typing that
// into a money field silently saved 4 (the <input type="number"> dropped the
// letter) or cleared the field on paste. This turns the shorthand into the
// absolute dollar figure it means.
//
// The suffix table deliberately mirrors the backend's `_NUM_MULT` in
// `backend/apps/api/app/routes/field_definitions.py` so a value typed in the
// UI and a value extracted from an LLM fill resolve identically — including
// `m` and `mm` both meaning million.
//
// No React, no DOM: pure functions, so `test/num_input.test.mjs` can load
// this file with a stub `window` and exercise it directly.

const NUM_INPUT_MULT = {
  k: 1e3, thousand: 1e3,
  m: 1e6, mm: 1e6, million: 1e6,
  b: 1e9, billion: 1e9,
};

// One number, optionally comma-grouped, optionally decimal, optionally
// suffixed — and nothing else. Anchored at both ends on purpose: partial
// garbage ("4M5", "4kk", "1.2.3") has to FAIL rather than match its leading
// digits, which is the whole bug we're fixing.
const NUM_INPUT_RE = /^(-?(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?)\s*(million|billion|thousand|mm|m|b|k)?$/i;

// Parse a user-typed money string into an absolute number.
//
//   '4M'        → 4000000       '100k'      → 100000
//   '1.5M'      → 1500000       '2.25k'     → 2250
//   '$1.5M'     → 1500000       '4,000,000' → 4000000
//   '~7M'       → 7000000       '4000000'   → 4000000
//
// Returns:
//   number     — parsed successfully
//   null       — empty/whitespace-only input, i.e. "clear this field"
//   undefined  — NOT A NUMBER. Callers must leave the previous value alone.
//
// The three outcomes are deliberately distinguishable: the old behaviour
// turned bad input into a plausible-looking wrong number, so "invalid" must
// never be mistakable for 0 or for null.
const parseNumInput = (raw) => {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (s === '') return null;
  // Tolerate what people actually paste: a leading "~" (the grids print one
  // in front of company-estimate figures — see EstMoneyCell — so it comes
  // back with any retyped value) and a currency prefix on either side of the
  // sign ("$4M", "-$4M", "$-4M"). A bare "~" or "$" leaves nothing to parse
  // and falls through to the invalid branch below.
  const body = s.replace(/^~\s*/, '').replace(/^(-?)\s*\$\s*/, '$1');
  const m = NUM_INPUT_RE.exec(body);
  if (!m) return undefined;
  const n = Number(m[1].replace(/,/g, ''));
  if (!Number.isFinite(n)) return undefined;
  const mult = m[2] ? NUM_INPUT_MULT[m[2].toLowerCase()] : 1;
  // Round to cents: 0.1 * 1e9 is 100000000.00000001 in binary floating point,
  // and a stray fraction of a cent has no business reaching the API.
  return Math.round(n * mult * 100) / 100;
};

// Render a parsed number back into the input as comma-grouped digits, so
// "4M" visibly becomes "4,000,000" on blur and the interpretation is
// self-evident before anyone saves.
const formatNumInput = (n) => {
  if (n == null || !Number.isFinite(n)) return '';
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

Object.assign(window, { parseNumInput, formatNumInput });
